/**
 * BulkUploadPage.js
 * Kitchen → Menu → Bulk Menu Upload
 *
 * Flow:
 *  1. User drags/selects a .csv / .xls / .xlsx file
 *  2. File is sent to backend (?dry_run=true) → preview with per-row validation
 *  3. User reviews the preview table and picks duplicate-handling option
 *  4. User clicks "Import" → backend (?dry_run=false) → import
 *  5. Result summary shown, with error-report download
 */

import React, { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload, FileText, X, CheckCircle, AlertCircle,
  ArrowLeft, Download, RotateCcw, Eye,
} from 'lucide-react';
import kitchenApi from '../services/kitchenApiService';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

// ── Small helpers ─────────────────────────────────────────────────────────────

const fmtBytes = (b) => {
  if (b < 1024)       return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(2)} MB`;
};

const STATUS_META = {
  valid:            { label: '✓ Valid',            color: '#15803D', bg: '#DCFCE7' },
  duplicate_skip:   { label: '⊘ Duplicate (skip)', color: '#92400E', bg: '#FEF3C7' },
  duplicate_update: { label: '↻ Will update',       color: '#1D4ED8', bg: '#DBEAFE' },
  duplicate_create: { label: '+ Create copy',       color: '#6D28D9', bg: '#EDE9FE' },
  error:            { label: '✕ Error',             color: '#DC2626', bg: '#FEE2E2' },
};

// Download a string as a file
function downloadText(content, filename, mimeType = 'text/plain') {
  const blob = new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// Generate CSV template text
function getTemplateCsv() {
  const header  = 'Category Name,Item Name,Description,Price,Food Type';
  const example1 = 'Biryani,Chicken Biryani,Hyderabadi dum biryani with tender chicken,14.99,Non-Vegetarian';
  const example2 = 'Biryani,Veg Biryani,Fragrant basmati rice with vegetables,11.99,Vegetarian';
  const example3 = 'Starters,Chicken 65,Crispy spicy fried chicken,10.99,Non-Vegetarian';
  return [header, example1, example2, example3].join('\n');
}

// Download XLSX template using SheetJS
async function downloadXlsxTemplate() {
  try {
    const XLSX = await import('xlsx');
    const ws = XLSX.utils.aoa_to_sheet([
      ['Category Name', 'Item Name', 'Description', 'Price', 'Food Type'],
      ['Biryani', 'Chicken Biryani', 'Hyderabadi dum biryani with tender chicken', 14.99, 'Non-Vegetarian'],
      ['Biryani', 'Veg Biryani', 'Fragrant basmati rice with vegetables', 11.99, 'Vegetarian'],
      ['Starters', 'Chicken 65', 'Crispy spicy fried chicken', 10.99, 'Non-Vegetarian'],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Menu Items');
    XLSX.writeFile(wb, 'menu_upload_template.xlsx');
  } catch {
    // Fallback to CSV if xlsx unavailable
    downloadText(getTemplateCsv(), 'menu_upload_template.csv', 'text/csv');
  }
}

// Download error report as CSV
function downloadErrorReport(errorRows) {
  const lines = [
    'Row Number,Category Name,Item Name,Description,Price,Food Type,Error',
    ...errorRows.map(r =>
      [r.row, r.category, r.item_name, r.description, r.price, r.food_type, r.error]
        .map(v => `"${String(v || '').replace(/"/g, '""')}"`)
        .join(',')
    ),
  ];
  downloadText(lines.join('\n'), 'bulk_upload_errors.csv', 'text/csv');
}

// ── Main component ────────────────────────────────────────────────────────────

const STEP = { IDLE: 0, PREVIEWING: 1, PREVIEW_READY: 2, IMPORTING: 3, DONE: 4 };

export default function BulkUploadPage() {
  const navigate = useNavigate();

  // File state
  const [file,     setFile]    = useState(null);
  const [fileErr,  setFileErr] = useState('');
  const [isDragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  // Wizard step
  const [step,   setStep]   = useState(STEP.IDLE);
  const [apiErr, setApiErr] = useState('');

  // Preview data from dry_run
  const [preview,     setPreview]     = useState([]);
  const [previewStat, setPreviewStat] = useState({ total: 0, valid: 0, errors: 0 });
  const [errorRows,   setErrorRows]   = useState([]);
  const [newCatsToCreate, setNewCatsToCreate] = useState([]); // categories that will be auto-created

  // Import options
  const [dupHandling,  setDupHandling]  = useState('skip');
  const [skipOnError,  setSkipOnError]  = useState(true);

  // Import progress
  const [progress,   setProgress]   = useState(0);  // 0-100

  // Import result
  const [result, setResult] = useState(null);

  // ── File selection ──────────────────────────────────────────────────────────
  const validateFile = useCallback((f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith('.csv') && !name.endsWith('.xlsx') && !name.endsWith('.xls')) {
      setFileErr('Unsupported file format. Please upload a CSV or Excel (.xls/.xlsx) file.');
      return;
    }
    if (f.size > MAX_FILE_SIZE_BYTES) {
      setFileErr(`File size exceeds the allowed limit of 10 MB. (File is ${fmtBytes(f.size)})`);
      return;
    }
    setFileErr('');
    setFile(f);
    setStep(STEP.IDLE);
    setPreview([]);
    setResult(null);
    setApiErr('');
  }, []);

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer?.files?.[0];
    if (f) validateFile(f);
  }, [validateFile]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (f) validateFile(f);
  };

  const handleRemoveFile = () => {
    setFile(null);
    setFileErr('');
    setStep(STEP.IDLE);
    setPreview([]);
    setNewCatsToCreate([]);
    setResult(null);
    setApiErr('');
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Preview (dry_run) ───────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (!file) return;
    setStep(STEP.PREVIEWING);
    setApiErr('');
    setProgress(0);

    const res = await kitchenApi.bulkUploadMenu(file, { dryRun: true, duplicateHandling: dupHandling });

    if (!res.success) {
      setApiErr(res.error || 'Failed to validate the file.');
      setStep(STEP.IDLE);
      return;
    }

    const d = res.data;
    if (d.missing_columns) {
      setApiErr(`The uploaded file is missing required columns: ${d.missing_columns.join(', ')}`);
      setStep(STEP.IDLE);
      return;
    }

    setPreview(d.preview || []);
    setErrorRows(d.errors || []);
    setNewCatsToCreate(d.categories_to_create || []);
    setPreviewStat({ total: d.total_rows, valid: d.valid_rows, errors: d.error_rows });
    setStep(STEP.PREVIEW_READY);
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file) return;
    if (!skipOnError && errorRows.length > 0) {
      setApiErr('The file contains errors. Check "Import valid rows and skip errors" to continue anyway.');
      return;
    }
    setStep(STEP.IMPORTING);
    setApiErr('');
    setProgress(0);

    const res = await kitchenApi.bulkUploadMenu(file, {
      dryRun: false,
      duplicateHandling: dupHandling,
      onProgress: (evt) => {
        if (evt.total) setProgress(Math.round((evt.loaded / evt.total) * 100));
      },
    });

    setProgress(100);

    if (!res.success) {
      setApiErr(res.error || 'Import failed. Please try again.');
      setStep(STEP.PREVIEW_READY);
      return;
    }

    setResult(res.data);
    setStep(STEP.DONE);
  };

  // ── Reset ───────────────────────────────────────────────────────────────────
  const handleReset = () => {
    setFile(null);
    setFileErr('');
    setStep(STEP.IDLE);
    setPreview([]);
    setErrorRows([]);
    setNewCatsToCreate([]);
    setResult(null);
    setApiErr('');
    setProgress(0);
    if (fileRef.current) fileRef.current.value = '';
  };

  // ── Render helpers ──────────────────────────────────────────────────────────
  const isBusy = step === STEP.PREVIEWING || step === STEP.IMPORTING;

  const validCount = preview.filter(r => r.status !== 'error' && r.status !== 'duplicate_skip').length;
  const dupSkipCount = preview.filter(r => r.status === 'duplicate_skip').length;

  // ── UI ──────────────────────────────────────────────────────────────────────
  return (
    <div style={{ maxWidth: 960, margin: '0 auto' }}>

      {/* Page header */}
      <div className="k-page-header">
        <div>
          <button
            className="k-btn k-btn-sm k-btn-ghost"
            onClick={() => navigate('/kitchen/menu')}
            style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 4 }}
          >
            <ArrowLeft size={14} /> Back to Menu
          </button>
          <h1 className="k-page-title">Bulk Menu Upload</h1>
          <p className="k-page-sub">Upload a CSV or Excel file to add multiple menu items at once.</p>
        </div>

        {/* Template downloads */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button
            className="k-btn k-btn-ghost k-btn-sm"
            onClick={() => downloadText(getTemplateCsv(), 'menu_upload_template.csv', 'text/csv')}
          >
            <Download size={14} /> CSV Template
          </button>
          <button
            className="k-btn k-btn-ghost k-btn-sm"
            onClick={downloadXlsxTemplate}
          >
            <Download size={14} /> Excel Template
          </button>
        </div>
      </div>

      {/* ── DONE state ── */}
      {step === STEP.DONE && result && (
        <div className="k-card" style={{ marginBottom: 20 }}>
          <div style={{ textAlign: 'center', padding: '16px 0 8px' }}>
            <CheckCircle size={40} color="#10B981" />
            <h2 style={{ marginTop: 10, fontSize: 18, fontWeight: 700 }}>
              {result.failed > 0 ? 'Import completed with some errors' : 'Bulk Upload Completed'}
            </h2>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, margin: '20px 0' }}>
            {[
              { label: 'Items imported',  value: result.imported,           color: '#10B981', bg: '#ECFDF5' },
              { label: 'Items updated',   value: result.updated,            color: '#3B82F6', bg: '#EFF6FF' },
              { label: 'Duplicates skipped', value: result.duplicates_skipped, color: '#F59E0B', bg: '#FFFBEB' },
              { label: 'Rows with errors', value: result.failed,            color: '#EF4444', bg: '#FEF2F2' },
              { label: 'Total processed', value: result.total_rows,         color: '#64748B', bg: '#F8FAFC' },
              ...(result.categories_created_count > 0 ? [{
                label: 'New categories created',
                value: result.categories_created_count,
                color: '#7C3AED',
                bg: '#F5F3FF',
              }] : []),
            ].map(({ label, value, color, bg }) => (
              <div key={label} style={{ background: bg, border: `1px solid ${color}22`, borderRadius: 10, padding: '14px 16px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 800, color }}>{value ?? 0}</div>
                <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>{label}</div>
              </div>
            ))}
          </div>

          {result.categories_created?.length > 0 && (
            <div style={{ marginBottom: 14, padding: '10px 14px', background: '#F5F3FF', borderRadius: 8, border: '1px solid #DDD6FE', fontSize: 13 }}>
              <strong style={{ color: '#7C3AED' }}>✦ Auto-created categories:</strong>{' '}
              <span style={{ color: '#4B5563' }}>{result.categories_created.join(', ')}</span>
            </div>
          )}

          {result.errors?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <button className="k-btn k-btn-ghost" onClick={() => downloadErrorReport(result.errors)}>
                <Download size={14} /> Download Error Report
              </button>
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            <button className="k-btn k-btn-primary" onClick={() => navigate('/kitchen/menu')}>
              View Menu
            </button>
            <button className="k-btn k-btn-ghost" onClick={handleReset}>
              <RotateCcw size={14} /> Upload Another File
            </button>
          </div>
        </div>
      )}

      {/* ── IMPORTING progress ── */}
      {step === STEP.IMPORTING && (
        <div className="k-card" style={{ marginBottom: 20, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 16 }}>Importing menu items…</div>
          <div style={{ background: '#F1F5F9', borderRadius: 999, height: 10, overflow: 'hidden', maxWidth: 400, margin: '0 auto 10px' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'var(--k-primary)', transition: 'width .3s', borderRadius: 999 }} />
          </div>
          <div style={{ fontSize: 12, color: 'var(--k-text-sub)' }}>{progress}% complete</div>
        </div>
      )}

      {/* ── PREVIEWING spinner ── */}
      {step === STEP.PREVIEWING && (
        <div className="k-card" style={{ marginBottom: 20, textAlign: 'center', padding: 32 }}>
          <div className="k-spinner" style={{ margin: '0 auto 12px' }} />
          <div style={{ fontSize: 14, color: 'var(--k-text-sub)' }}>Validating menu items…</div>
        </div>
      )}

      {step !== STEP.DONE && (
        <>
          {/* ── File drop zone ── */}
          {!file && (
            <div className="k-card" style={{ marginBottom: 20 }}>
              <div
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={handleFileDrop}
                onClick={() => fileRef.current?.click()}
                style={{
                  border: `2px dashed ${isDragging ? 'var(--k-primary)' : 'var(--k-border)'}`,
                  borderRadius: 12,
                  padding: '48px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragging ? 'var(--k-primary-bg)' : 'var(--k-surface)',
                  transition: 'all .2s',
                }}
                role="button"
                tabIndex={0}
                aria-label="Drop file or click to choose file"
                onKeyDown={(e) => e.key === 'Enter' && fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept=".csv,.xls,.xlsx"
                  style={{ display: 'none' }}
                  onChange={handleFileChange}
                  aria-label="Choose a CSV or Excel file"
                />
                <Upload size={36} color="var(--k-text-muted)" style={{ marginBottom: 12 }} />
                <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>Upload your menu file</div>
                <div style={{ color: 'var(--k-text-sub)', marginBottom: 16 }}>Drag & drop your file here, or click to choose</div>
                <button
                  className="k-btn k-btn-primary"
                  onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                >
                  Choose File
                </button>
                <div style={{ marginTop: 16, fontSize: 12, color: 'var(--k-text-muted)' }}>
                  Supported: CSV, XLS, XLSX &nbsp;·&nbsp; Max size: 10 MB
                </div>
              </div>

              {fileErr && (
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--k-danger)', fontSize: 13 }} role="alert">
                  <AlertCircle size={16} /> {fileErr}
                </div>
              )}
            </div>
          )}

          {/* ── Selected file info ── */}
          {file && (
            <div className="k-card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 12 }}>Selected File</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', background: 'var(--k-surface-2)', borderRadius: 8 }}>
                <FileText size={28} color="var(--k-primary)" />
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600 }}>{file.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--k-text-sub)' }}>{fmtBytes(file.size)}</div>
                </div>
                {!isBusy && (
                  <button className="k-btn k-btn-sm k-btn-ghost" onClick={handleRemoveFile} aria-label="Remove file">
                    <X size={14} /> Remove
                  </button>
                )}
              </div>

              {/* Import options */}
              {step !== STEP.IMPORTING && (
                <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div>
                    <label className="k-label" htmlFor="dup-handling">Duplicate Handling</label>
                    <select
                      id="dup-handling"
                      className="k-select"
                      style={{ maxWidth: 300 }}
                      value={dupHandling}
                      onChange={(e) => setDupHandling(e.target.value)}
                      disabled={isBusy}
                    >
                      <option value="skip">Skip duplicate items (recommended)</option>
                      <option value="update">Update existing items</option>
                      <option value="duplicate">Import as duplicate</option>
                    </select>
                  </div>

                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                    <input
                      type="checkbox"
                      checked={skipOnError}
                      onChange={(e) => setSkipOnError(e.target.checked)}
                      disabled={isBusy}
                    />
                    Import valid rows and skip rows with errors
                  </label>
                </div>
              )}

              {step === STEP.IDLE && !isBusy && (
                <div style={{ marginTop: 16 }}>
                  <button className="k-btn k-btn-primary" onClick={handlePreview} disabled={!!fileErr}>
                    <Eye size={15} /> Preview &amp; Validate
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── API error ── */}
          {apiErr && (
            <div className="k-card" style={{ marginBottom: 20, borderColor: 'var(--k-danger)', background: 'var(--k-danger-bg)' }} role="alert">
              <div style={{ display: 'flex', gap: 8, color: 'var(--k-danger)', fontWeight: 500 }}>
                <AlertCircle size={18} /> {apiErr}
              </div>
            </div>
          )}

          {/* ── Preview table ── */}
          {step === STEP.PREVIEW_READY && preview.length > 0 && (
            <div className="k-card" style={{ marginBottom: 20 }}>
                <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>Preview</div>
                  <div style={{ fontSize: 13, color: 'var(--k-text-sub)' }}>
                    Total: <strong>{previewStat.total}</strong>&nbsp; ·&nbsp;
                    Valid: <strong style={{ color: '#15803D' }}>{previewStat.valid}</strong>&nbsp; ·&nbsp;
                    Errors: <strong style={{ color: '#DC2626' }}>{previewStat.errors}</strong>
                    {dupSkipCount > 0 && <> &nbsp; · &nbsp; Skip duplicates: <strong style={{ color: '#92400E' }}>{dupSkipCount}</strong></>}
                    {newCatsToCreate.length > 0 && (
                      <> &nbsp; · &nbsp; <strong style={{ color: '#7C3AED' }}>✦ {newCatsToCreate.length} new category{newCatsToCreate.length !== 1 ? 'ies' : ''} will be auto-created</strong></>
                    )}
                  </div>
                </div>
                {errorRows.length > 0 && (
                  <button className="k-btn k-btn-ghost k-btn-sm" onClick={() => downloadErrorReport(errorRows)}>
                    <Download size={13} /> Error Report
                  </button>
                )}
              </div>

              {/* New-categories notice */}
              {newCatsToCreate.length > 0 && (
                <div style={{ marginBottom: 14, padding: '10px 14px', background: '#F5F3FF', borderRadius: 8, border: '1px solid #DDD6FE', fontSize: 13 }}>
                  <strong style={{ color: '#7C3AED' }}>✦ New categories that will be automatically created:</strong>{' '}
                  <span style={{ color: '#4B5563' }}>{newCatsToCreate.join(', ')}</span>
                </div>
              )}

              {/* Scrollable table */}
              <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: 'var(--k-surface-2)' }}>
                      {['Row', 'Category', 'Item Name', 'Description', 'Price', 'Food Type', 'Status'].map(h => (
                        <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap', borderBottom: '1px solid var(--k-border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((r, i) => {
                      const meta = STATUS_META[r.status] || STATUS_META.error;
                      return (
                        <tr key={i} style={{ borderBottom: '1px solid var(--k-border-light)', background: r.status === 'error' ? '#FFF5F5' : undefined }}>
                          <td style={{ padding: '8px 12px', color: 'var(--k-text-sub)' }}>{r.row}</td>
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>
                            {r.category || '—'}
                            {r.new_category && (
                              <span style={{ marginLeft: 6, background: '#EDE9FE', color: '#7C3AED', fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999 }}>NEW</span>
                            )}
                          </td>
                          <td style={{ padding: '8px 12px', fontWeight: 500 }}>{r.item_name || '—'}</td>
                          <td style={{ padding: '8px 12px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--k-text-sub)' }} title={r.description}>{r.description || '—'}</td>
                          <td style={{ padding: '8px 12px' }}>{r.price || '—'}</td>
                          <td style={{ padding: '8px 12px' }}>{r.food_type || '—'}</td>
                          <td style={{ padding: '8px 12px', whiteSpace: 'nowrap' }}>
                            <span style={{ background: meta.bg, color: meta.color, padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 600 }}>
                              {meta.label}
                            </span>
                            {r.errors?.length > 0 && (
                              <div style={{ marginTop: 3, fontSize: 11, color: '#DC2626' }}>
                                {r.errors.join(' · ')}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Import controls */}
              <div style={{ marginTop: 20, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <button
                  className="k-btn k-btn-primary"
                  onClick={handleImport}
                  disabled={validCount === 0 && dupHandling !== 'update'}
                  aria-label={`Import ${validCount} valid items`}
                >
                  Import {validCount} Valid Item{validCount !== 1 ? 's' : ''}
                </button>
                <button className="k-btn k-btn-ghost" onClick={handleReset}>
                  Cancel
                </button>

                {errorRows.length > 0 && !skipOnError && (
                  <span style={{ fontSize: 12, color: 'var(--k-danger)' }}>
                    ⚠ File contains errors. Check &quot;skip errors&quot; to continue.
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Preview empty (all valid) already handled by table rendering */}
        </>
      )}

      {/* ── Format reference card ── */}
      {step === STEP.IDLE && !file && (
        <div className="k-card" style={{ marginBottom: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Required File Format</div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: 'var(--k-surface-2)' }}>
                  {['Category Name', 'Item Name', 'Description', 'Price', 'Food Type'].map(h => (
                    <th key={h} style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, borderBottom: '1px solid var(--k-border)', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {[
                  ['Biryani', 'Chicken Biryani', 'Hyderabadi dum biryani', '14.99', 'Non-Vegetarian'],
                  ['Biryani', 'Veg Biryani',     'Vegetable dum biryani',  '11.99', 'Vegetarian'],
                  ['Starters', 'Chicken 65',     'Crispy spicy chicken',   '10.99', 'Non-Vegetarian'],
                ].map((row, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid var(--k-border-light)' }}>
                    {row.map((cell, j) => (
                      <td key={j} style={{ padding: '8px 12px', color: j === 0 ? 'var(--k-primary)' : undefined, fontWeight: j < 2 ? 500 : undefined }}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <ul style={{ marginTop: 14, paddingLeft: 18, fontSize: 13, color: 'var(--k-text-sub)', lineHeight: 1.8 }}>
            <li>The <strong>Category Name</strong> is case-insensitive. If a category does not exist, it will be <strong>automatically created</strong> during the upload.</li>
            <li><strong>Food Type</strong> must be <code>Vegetarian</code> or <code>Non-Vegetarian</code> (or common abbreviations).</li>
            <li><strong>Price</strong> can include a <code>$</code> symbol — e.g. <code>$14.99</code> or <code>14.99</code>.</li>
            <li>Duplicate items in the same category are handled by your chosen <em>Duplicate Handling</em> option.</li>
          </ul>
        </div>
      )}
    </div>
  );
}
